/**
 * SCK-738 — Beta invite email template.
 *
 * Renders the branded clue2app Beta invite email as `{html, text, subject,
 * preheader}` for operators to copy out of CopyLinkModal and paste into
 * Gmail/Outlook. The HTML half uses inline styles only (no CSS variables,
 * no <style> block, no web fonts) so it survives the mail-client CSS
 * stripper. The plain-text half mirrors §17 of the brand kit's
 * DESIGN-SYSTEM.md.
 *
 * Brand kit source of truth:
 *   https://claude.ai/code/artifact/88bac7da-e47d-49d4-931b-a3e90ea2c295
 *   - §15 for email font stacks and the bulletproof table-cell button
 *   - §17 for subject + preheader + plain-text copy
 *
 * Colors inlined verbatim (not via tokens.css) because mail clients strip
 * CSS variables. If 09-design-system/tokens.css changes, update here too.
 */

const C = {
  navy: "#0E1A3A",
  navyDeep: "#080F24",
  ink: "#0E1A3A",
  ink2: "#2A3A61",
  muted: "#5B6B8F",
  rule: "#D9DEEA",
  soft: "#E8EEF9",
  surface: "#FFFFFF",
  bg: "#F4F5FA",
  teal: "#72DCCA",
  blue: "#1E8AEB",
  action: "#C93C2E",
  actionHover: "#A9311F",
  onAction: "#FFFFFF",
  gold: "#FFC24B",
  warningSoft: "#FFF3D9",
};

const FONT_HEAD =
  "Poppins, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif";
const FONT_BODY =
  "Inter, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif";
const FONT_MONO =
  "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

const SUBJECT = "You're in: clue2app beta";
const PREHEADER =
  "Deploy your first app in minutes. Agents watch it from there.";
const VIDEO_URL = "https://youtu.be/586OvEUBluI";

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatExpiry(expiresOn) {
  if (!expiresOn) return "";
  try {
    const d = new Date(expiresOn);
    return d.toUTCString().replace(" GMT", " UTC");
  } catch {
    return String(expiresOn);
  }
}

/**
 * Build the branded beta-invite content.
 *
 * @param {object} args
 * @param {string} args.redemptionUrl   One-shot URL the invitee opens to redeem.
 * @param {string} [args.email]         Invitee email, shown in the preview + plain-text footer.
 * @param {string} [args.expiresOn]     ISO timestamp; rendered as "Expires <utc>" if set.
 * @param {string} [args.firstName]     Optional personalization; falls back to "there".
 *
 * @returns {{html: string, text: string, subject: string, preheader: string}}
 */
export function renderBetaInviteEmail({
  redemptionUrl,
  email,
  expiresOn,
  firstName,
} = {}) {
  const url = redemptionUrl || "";
  const greetingName = (firstName || "").trim();
  const greeting = greetingName
    ? `Hi ${escapeHtml(greetingName)},`
    : "You're in.";
  const expiryLine = formatExpiry(expiresOn);

  const html = `<div style="margin:0;padding:0;background:${C.bg};font-family:${FONT_BODY};color:${C.ink};line-height:1.55">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;height:0;width:0;mso-hide:all">${escapeHtml(PREHEADER)}&#847;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;</div>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${C.bg}">
  <tr><td align="center" style="padding:24px 12px">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="640" style="width:640px;max-width:100%;background:${C.surface};border:1px solid ${C.rule};border-radius:14px;overflow:hidden">

      <!-- HERO -->
      <tr><td style="background:${C.navy};padding:40px 40px 36px;color:#EEF2FB">
        <div style="font-family:${FONT_HEAD};font-weight:600;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:${C.teal};margin-bottom:14px">● You're In · 30-Day Beta</div>
        <h1 style="margin:0 0 12px;font-family:${FONT_HEAD};font-weight:700;font-size:36px;line-height:1.1;letter-spacing:-0.02em;color:#ffffff">Welcome Aboard 🚀</h1>
        <p style="margin:0;font-family:${FONT_BODY};font-size:16px;line-height:1.6;color:#AAB6D6;max-width:48em">You're one of the first explorers of the Agentic Intelligent Platform. Over the next 30 days, your real workflows, your bugs, and your blunt feedback will shape what this becomes.</p>
      </td></tr>

      <!-- BASICS -->
      <tr><td style="padding:36px 40px 24px">
        <div style="font-family:${FONT_MONO};font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${C.muted};margin:0 0 14px">The Basics</div>
        <h2 style="margin:0 0 8px;font-family:${FONT_HEAD};font-weight:600;font-size:22px;letter-spacing:-0.01em;color:${C.ink}">Test things. Break things. Tell us.</h2>
        <p style="margin:0 0 20px;color:${C.muted};font-size:14px">As a Beta user you have exclusive access to try features, push boundaries, and tell us what works — and what doesn't.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td width="33%" valign="top" style="padding:14px;background:${C.soft};border-left:3px solid ${C.teal};border-radius:8px">
              <div style="font-family:${FONT_MONO};font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:${C.muted};margin-bottom:6px">Goal</div>
              <div style="font-family:${FONT_HEAD};font-weight:600;font-size:14px;color:${C.ink};line-height:1.3">Use the platform in your real workflows and give us raw feedback.</div>
            </td>
            <td width="8"></td>
            <td width="33%" valign="top" style="padding:14px;background:${C.soft};border-left:3px solid ${C.teal};border-radius:8px">
              <div style="font-family:${FONT_MONO};font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:${C.muted};margin-bottom:6px">Duration</div>
              <div style="font-family:${FONT_HEAD};font-weight:600;font-size:14px;color:${C.ink};line-height:1.3">30 days from your first login.</div>
            </td>
            <td width="8"></td>
            <td width="33%" valign="top" style="padding:14px;background:${C.soft};border-left:3px solid ${C.teal};border-radius:8px">
              <div style="font-family:${FONT_MONO};font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:${C.muted};margin-bottom:6px">Feedback</div>
              <div style="font-family:${FONT_HEAD};font-weight:600;font-size:14px;color:${C.ink};line-height:1.3">In-app button or <a href="mailto:beta@clue2solve.com" style="color:${C.action};text-decoration:none">beta@clue2solve.com</a></div>
            </td>
          </tr>
        </table>
      </td></tr>

      <!-- VIDEO -->
      <tr><td style="padding:12px 40px 32px">
        <div style="font-family:${FONT_MONO};font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${C.muted};margin:0 0 10px">Start Here · 3 min</div>
        <h2 style="margin:0 0 6px;font-family:${FONT_HEAD};font-weight:600;font-size:22px;color:${C.ink}">Deploy your first app.</h2>
        <p style="margin:0 0 14px;color:${C.muted};font-size:14px">A quick walkthrough: from a Git repo to a live URL on clue2app.</p>
        <a href="${escapeHtml(VIDEO_URL)}" style="text-decoration:none;color:#EEF2FB">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${C.navyDeep};border-radius:12px;overflow:hidden">
            <tr><td align="center" style="padding:38px 24px">
              <div style="display:inline-block;width:56px;height:56px;line-height:56px;border-radius:50%;background:${C.action};color:#ffffff;font-size:22px;text-align:center;font-family:${FONT_HEAD};font-weight:700">▶</div>
              <div style="margin-top:14px;font-family:${FONT_HEAD};font-weight:600;font-size:15px;color:#EEF2FB">First App Deployment · Introduction</div>
              <div style="margin-top:6px;font-family:${FONT_MONO};font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:${C.teal}">Watch on YouTube</div>
            </td></tr>
          </table>
        </a>
      </td></tr>

      <!-- CTA — bulletproof table-cell button, §15 of the kit -->
      <tr><td style="background:${C.soft};padding:28px 40px 32px;border-top:1px solid ${C.rule};border-bottom:1px solid ${C.rule}" align="center">
        <p style="margin:0 0 14px;color:${C.muted};font-size:14px">Ready to dive in?</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto">
          <tr><td align="center" bgcolor="${C.action}" style="background:${C.action};border-radius:10px">
            <a href="${escapeHtml(url)}" style="display:inline-block;padding:14px 32px;font-family:${FONT_HEAD};font-weight:600;font-size:16px;color:${C.onAction};text-decoration:none;border-radius:10px">Log In →</a>
          </td></tr>
        </table>
        ${
          expiryLine
            ? `<p style="margin:14px 0 0;font-family:${FONT_MONO};font-size:11px;color:${C.muted}">Link expires ${escapeHtml(expiryLine)}</p>`
            : ""
        }
      </td></tr>

      <!-- T&C summary -->
      <tr><td style="padding:32px 40px;color:${C.ink}">
        <div style="font-family:${FONT_MONO};font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${C.muted};margin:0 0 10px">Beta Access · Terms &amp; Conditions</div>
        <h2 style="margin:0 0 10px;font-family:${FONT_HEAD};font-weight:600;font-size:18px;color:${C.ink}">By logging in, you agree to the following.</h2>
        <p style="margin:0 0 10px;font-size:13.5px;color:${C.muted}">Limited, non-exclusive, revocable license for evaluation only (30 days). Services are "AS IS" with no warranties. Don't upload sensitive personal data, proprietary code, or production data; any feedback you share may be used by Clue2solve freely. Platform features, UI and performance metrics are confidential — no public benchmarks, screenshots or reviews without written consent. All platform IP (code, models, workflows, design) remains Clue2solve's; no reverse-engineering, cloning, scraping, or using outputs to train competing systems; these IP restrictions survive after the Beta ends. We may modify, suspend or end access with 7 days' notice (immediate for IP violations); workspace data may be deleted after 30 days. Not liable for indirect or consequential damages. Governed by California law; disputes exclusively in Santa Clara County courts.</p>
        <div style="margin-top:16px;padding:12px 14px;background:${C.warningSoft};border-left:3px solid ${C.gold};border-radius:6px;font-size:12.5px;color:${C.ink2}">
          <b style="color:${C.ink}">Survival.</b> Feedback license, confidentiality, IP restrictions, limitation of liability, and governing law survive expiration or termination.
        </div>
      </td></tr>

      <!-- FOOTER -->
      <tr><td style="background:${C.navy};padding:24px 40px 28px;color:#AAB6D6;font-size:12px">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td style="color:#ffffff;font-family:${FONT_BODY};font-weight:500">clue2app.ai</td>
            <td align="right" style="font-family:${FONT_BODY};color:#AAB6D6">Agentic Intelligent Platform · Beta</td>
          </tr>
          <tr>
            <td colspan="2" style="padding-top:8px;font-family:${FONT_BODY};color:#AAB6D6">Questions? <a href="mailto:beta@clue2solve.com" style="color:#AAB6D6">beta@clue2solve.com</a></td>
          </tr>
        </table>
      </td></tr>

    </table>
  </td></tr>
</table>
</div>`;

  const text = [
    `${greeting}`,
    "",
    "Your clue2app beta access is ready. Deploy your first app in a few clicks, and agents watch it from there.",
    "",
    `Start here: ${url}`,
    expiryLine ? `(This link expires ${expiryLine})` : null,
    "",
    `Watch the 3-minute intro: ${VIDEO_URL}`,
    "",
    "Test things. Break things. Tell us — reply to this email or write beta@clue2solve.com.",
    "",
    "Terms (short version): Limited 30-day beta license for evaluation only. Services are AS IS. No sensitive data, proprietary code, or production data; feedback may be used freely by Clue2solve. Platform is confidential — no public benchmarks or screenshots without consent. All IP remains Clue2solve's; no cloning, reverse-engineering, scraping, or training competing systems (these survive the beta). We may end access with 7 days' notice (immediate for IP violations); workspace data may be deleted after 30 days. California law; Santa Clara County courts.",
    "",
    "clue2app.ai · Clue2solve Inc.",
    email ? `Sent to ${email}.` : null,
  ]
    .filter((line) => line !== null)
    .join("\n");

  return { html, text, subject: SUBJECT, preheader: PREHEADER };
}
